import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { BurgerMenu } from '../components/BurgerMenu';
import { useTheme } from '../App';
interface ProjectPageProps {
  project: {
    title: string;
    category: string | string[];
    year: string;
    image: string;
    thumbnail?: string;
    mediaType?: 'image' | 'video';
    description?: string;
  };
  onBack: () => void;
}
export function ProjectPage({
  project,
  onBack
}: ProjectPageProps) {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Get category display text
  const getCategoryDisplay = () => {
    if (Array.isArray(project.category)) {
      return project.category.join(', ');
    }
    return project.category;
  };
  // Check if this project has Video category
  const categories = Array.isArray(project.category) ? project.category : [project.category];
  const hasVideoCategory = categories.includes('Video');
  // Always show full video for Video category projects, otherwise use mediaType
  const shouldShowVideo = hasVideoCategory || project.mediaType === 'video';
  // Handle back to work - scroll to work section
  const handleBackToWork = () => {
    onBack();
    // Wait for navigation to complete, then scroll to work section
    setTimeout(() => {
      const workSection = document.getElementById('work');
      if (workSection) {
        workSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <div className={`min-h-screen flex flex-col selection:bg-white selection:text-black overflow-x-hidden transition-colors duration-500 ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`} data-id="element-444">
      <BurgerMenu data-id="element-445" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full p-8 flex justify-between items-center z-40 pointer-events-none" data-id="element-446">
        <div className="pointer-events-auto" data-id="element-447">
          <button onClick={handleBackToWork} className={`text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-448">
            Jav.
          </button>
        </div>
      </nav>

      {/* Main content - flex-grow to push footer down */}
      <main className="relative z-10 pt-32 pb-24 px-4 md:px-12 flex-grow" data-id="element-449">
        {/* Max-width container for content only */}
        <div className="max-w-[1300px] mx-auto" data-id="element-450">
          {/* Two column layout */}
          <div className="flex flex-col lg:flex-row gap-12" data-id="element-451">
            {/* Left: Media */}
            <div className="flex-1 lg:w-[70%]" data-id="element-452">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              ease: [0.33, 1, 0.68, 1]
            }} data-id="element-453">
                <div className="relative w-full bg-neutral-900 rounded-sm overflow-hidden" data-id="element-454">
                  {shouldShowVideo ? <video src={project.image} autoPlay loop muted playsInline controls className="w-full h-auto" data-id="element-455" /> : <img src={project.image} alt={project.title} className="w-full h-auto" data-id="element-456" />}
                </div>
              </motion.div>
            </div>

            {/* Right: Info Sidebar */}
            <div className="lg:w-[30%] lg:sticky lg:top-32 lg:self-start" data-id="element-457">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2,
              ease: [0.33, 1, 0.68, 1]
            }} className="space-y-8" data-id="element-458">
                {/* Title */}
                <div data-id="element-459">
                  <h1 className={`text-4xl md:text-5xl font-bold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-460">
                    {project.title}
                  </h1>
                </div>

                {/* Category */}
                <div className="border-t pt-6" style={{
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
              }} data-id="element-461">
                  <p className="text-xs uppercase tracking-widest font-light mb-2" style={{
                  color: isDark ? '#999' : '#666'
                }} data-id="element-462">
                    {Array.isArray(project.category) && project.category.length > 1 ? 'Categories' : 'Category'}
                  </p>
                  <p className={`text-lg ${isDark ? 'text-white' : 'text-black'}`} data-id="element-463">
                    {getCategoryDisplay()}
                  </p>
                </div>

                {/* Year */}
                <div className="border-t pt-6" style={{
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
              }} data-id="element-464">
                  <p className="text-xs uppercase tracking-widest font-light mb-2" style={{
                  color: isDark ? '#999' : '#666'
                }} data-id="element-465">
                    Year
                  </p>
                  <p className={`text-lg ${isDark ? 'text-white' : 'text-black'}`} data-id="element-466">
                    {project.year}
                  </p>
                </div>

                {/* About */}
                <div className="border-t pt-6" style={{
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
              }} data-id="element-467">
                  <p className="text-xs uppercase tracking-widest font-light mb-4" style={{
                  color: isDark ? '#999' : '#666'
                }} data-id="element-468">
                    About
                  </p>
                  <p className="text-base leading-relaxed" style={{
                  color: isDark ? 'rgb(163, 163, 163)' : '#666'
                }} data-id="element-469">
                    {project.description || `A minimal and elegant ${Array.isArray(project.category) ? project.category[0].toLowerCase() : project.category.toLowerCase()} project that showcases modern design principles and attention to detail. This work demonstrates a thoughtful approach to user experience and visual storytelling.`}
                  </p>
                </div>

                {/* Back Button */}
                <div className="pt-6" data-id="element-470">
                  <button onClick={handleBackToWork} className={`w-full px-8 py-4 font-bold rounded-full flex items-center justify-center gap-2 transition-opacity duration-300 hover:opacity-70 ${isDark ? 'bg-white text-black' : 'bg-black text-white'}`} data-id="element-471">
                    <ArrowLeft className="w-5 h-5" data-id="element-472" />
                    <span data-id="element-473">Back to Work</span>
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer - full width, always at bottom */}
      <footer className="relative z-10 w-full py-8 px-4 md:px-12 border-t mt-auto" style={{
      borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
    }} data-id="element-474">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs md:text-sm uppercase tracking-wide font-light" style={{
        color: isDark ? '#999' : '#666'
      }} data-id="element-475">
          <span data-id="element-476">© 2024 Jav.art — Buenos Aires, AR</span>
          <span data-id="element-477">All Rights Reserved</span>
        </div>
      </footer>
    </div>;
}